import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@lingui/macro';

import _ from 'lodash';
import Panel from '../../page-framework/panel/PanelDecorator';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import {
    businessServiceFilterId,
    siteFilterId,
    statusFilterId,
    testTypeFilterId,
    timeRangeFilterId,
} from '../../filters';
import { useDataService } from '../../../services/ApiDataService';
import { dateTimeFormatter } from '../../../utilities/FormattingUtilities';
import WebTestNowResultsMainPanel from './WebTestNowResultsMainPanel';
import { shouldPoll } from '../quick-test-panel/QuickTestHelpers';
import { buildLinkUrl } from '../../links/Link';
import { QUICK_TEST_STATUS, QUICKTEST_URL } from '../quick-test-panel/QuickTestConstants';

const title = t`Web Test Now Result`;

const POLLING_INTERVAL = 1000 * 6; // 6 seconds
const STATUS_LUT = {};
STATUS_LUT[QUICK_TEST_STATUS.INITIALIZING] = QUICK_TEST_STATUS.INITIALIZING;
STATUS_LUT[QUICK_TEST_STATUS.SETTING_UP] = QUICK_TEST_STATUS.SETTING_UP;
STATUS_LUT[QUICK_TEST_STATUS.RUNNING] = QUICK_TEST_STATUS.RUNNING;
STATUS_LUT[QUICK_TEST_STATUS.TEARING_DOWN] = QUICK_TEST_STATUS.TEARING_DOWN;
STATUS_LUT[QUICK_TEST_STATUS.COMPLETE] = QUICK_TEST_STATUS.COMPLETE;
STATUS_LUT[QUICK_TEST_STATUS.ERROR] = QUICK_TEST_STATUS.ERROR;
STATUS_LUT[QUICK_TEST_STATUS.CANCELED] = QUICK_TEST_STATUS.CANCELED;

const dataSource = {
    url: '/ipm/v1/admin/quicktests/',
    dataProperty: 'data',
    totalProperty: 'count',
    isResultsPage: true,
    numericMetrics: ['web_in', 'web_out', 'duration'],
    params: {
        searchFields: ['name'],
    },
};

function WebTestNowTestResultPanel(props) {
    dataSource.url = `/ipm/v1/admin/quicktests/${props.id}`;
    const dataService = useDataService();
    const [results, setResults] = useState();
    const [agentsById, setAgentsById] = useState({});
    const [, setIsPolling] = useState(true);
    const timerRef = useRef(null);
    const mountedRef = useRef(true);
    const isPollingRef = useRef(true);
    const { onResultsChange } = props;

    const getQuickTestById = useCallback(
        async (id) => {
            const url = buildLinkUrl({ target: QUICKTEST_URL }, { _id: id }, null);
            const response = await dataService.get(url, {
                query: JSON.stringify({
                    status: _.values(QUICK_TEST_STATUS),
                }),
            });
            return response;
        },
        [dataService]
    );

    /**
     * assign test fields to the scope and manipulate url if it doesnt have an http
     * @param test
     * @returns {*}
     */
    const assignTestFields = useCallback((test) => {
        const res = {};
        res.test = test;
        res.status = STATUS_LUT[test.status];
        res.showLoading = shouldPoll(test.status);
        res.createdAtFmt = dateTimeFormatter(test.createdAt);
        res.timedOut = QUICK_TEST_STATUS.ERROR === test.status && !_.isUndefined(test.forceCompletedAt);

        if (test.testConfig.url) {
            res.test.testConfig.url = test.testConfig.url;
        }

        setResults(res);
        return test;
    }, []);

    const setPolling = useCallback(
        (test) => {
            const tick = () => {
                if (!test?._id) return Promise.resolve();

                return getQuickTestById(test._id)
                    .then((latestTest) => assignTestFields(latestTest))
                    .then((latestTest) => {
                        if (!mountedRef.current || !isPollingRef.current) return Promise.resolve();

                        if (shouldPoll(latestTest.status)) {
                            setResults((prev) => ({ ...(prev || {}), testRunning: true }));
                            timerRef.current = setTimeout(tick, POLLING_INTERVAL);
                            return timerRef.current;
                        }

                        // looks like we are done with the test
                        setResults((prev) => ({ ...(prev || {}), testRunning: false }));
                        return Promise.resolve();
                    });
            };

            return tick();
        },
        [assignTestFields, getQuickTestById]
    );

    /**
     * Helper function to stop polling
     */
    function cancelPolling() {
        isPollingRef.current = false;
        setIsPolling(false);

        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }

    const determinePolling = useCallback(
        (test) => {
            if (shouldPoll(test.status)) {
                return setPolling(test);
            }
            // the test is either completed or errored out
            return test;
        },
        [setPolling]
    );

    useEffect(() => {
        mountedRef.current = true;
        isPollingRef.current = true;

        const initialize = async () => {
            const test = await dataService.get(dataSource.url);
            assignTestFields(test);
            determinePolling(test);
        };

        initialize();

        return () => {
            mountedRef.current = false;
            cancelPolling();
        };
    }, [dataService, assignTestFields, determinePolling]);

    useEffect(() => {
        if (typeof onResultsChange === 'function') {
            onResultsChange({ results, agentsById });
        }
    }, [results, agentsById, onResultsChange]);

    return <WebTestNowResultsMainPanel results={results} agentsById={agentsById} setAgentsById={setAgentsById} />;
}

WebTestNowTestResultPanel.propTypes = {
    ...panelPropTypes,
};

WebTestNowTestResultPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'WebTestResultPanel',
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, statusFilterId, siteFilterId, testTypeFilterId],
    },
})(WebTestNowTestResultPanel);
